import {
  type BusinessObjectMetadata,
  normalizeBusinessObjectMetadata,
} from "~/features/businessObjects/utils/normalizeMetadata";
import { parseRecordCapabilities } from "~/features/businessObjects/utils/recordCapabilities";

/**
 * Shared parsers for record-service responses.
 *
 * The backend returns dynamic-resource records in two related but slightly
 * different shapes depending on the route:
 *
 *  - `GET /…/{resource}/{version}`         → `BusinessObject[]` (bare)
 *  - `GET /…/{resource}/{version}/metadata`→ `[{ data, metadata }, …]`
 *  - `GET /…/{resource}/{version}/{pk}`         → `BusinessObject`
 *  - `GET /…/{resource}/{version}/{pk}/metadata`→ `{ data, metadata }`
 *
 * On top of that, our `customFetch` strips one outer `{ data: T }` envelope,
 * so callers see either the raw array/object or a once-wrapped variant
 * (`{ data: [...] }`) depending on whether the call went through
 * `customFetch` or `apiMakeRawRequest`. Historically each feature wrote its
 * own coercion; this module is the single source of truth.
 */

/** A single dynamic-resource record returned with metadata. */
export interface RecordWithMeta {
  data: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

/**
 * Describe a payload for the diagnostics below without leaking record contents
 * into the console.
 *
 * `null` is spelled out rather than handed to `Object.keys`: `typeof null` is
 * `"object"`, so the obvious one-liner throws a TypeError *inside* the error
 * report. The parsers this replaced carried exactly that latent bug.
 */
const describeShape = (raw: unknown): string => {
  if (raw === null) {
    return "null";
  }

  if (typeof raw === "object") {
    return `{ ${Object.keys(raw).join(", ")} }`;
  }

  return typeof raw;
};

/**
 * Report a payload shape we do not recognise, then recover from it.
 *
 * The lenient `[]` / `null` recovery is deliberate — callers treat absence as
 * "empty result", not "error" — but swallowing it silently is how a blank table
 * gets mistaken for a genuinely empty collection. Report first, recover second.
 *
 * Nothing is reported for `null` / `undefined` payloads: "no body at all" is the
 * ordinary 204 / 404 case, and every call site already resolves it to empty.
 */
export const reportUnexpectedShape = (scope: string, raw: unknown): void => {
  if (raw == null) {
    return;
  }

  console.error(`[businessObjects] Unexpected ${scope} response shape:`, describeShape(raw));
};

const isRecordWithMeta = (value: unknown): value is RecordWithMeta =>
  isPlainObject(value) && isPlainObject(value.data);

/** Table-mode collection queries return bare `BusinessObject` rows (no `{ data, metadata }`). */
const toRecordWithMeta = (value: unknown): RecordWithMeta | null => {
  if (isRecordWithMeta(value)) {
    return value;
  }

  if (isPlainObject(value) && !isMetadataEnvelope(value)) {
    return { data: value };
  }

  return null;
};

/**
 * A "metadata envelope" is an object with BOTH `data` and `metadata` keys —
 * the canonical shape returned by `/…/metadata` routes. We use the dual-key
 * presence as a disambiguator: a value with `data` alone might be a generic
 * `{ data: <thing> }` envelope OR a record whose BO body happens to have a
 * `data` field. With `metadata` present we can be confident it's the
 * metadata envelope.
 */
const isMetadataEnvelope = (value: unknown): value is RecordWithMeta =>
  isRecordWithMeta(value) && "metadata" in value;

/**
 * Coerce a record-service collection response to `RecordWithMeta[]`.
 *
 * Accepts:
 *   - `RecordWithMeta[]`                          (already-unwrapped)
 *   - `{ data: RecordWithMeta[] }`                 (one envelope)
 *   - `{ data: { data: RecordWithMeta[] } }`       (defensive — older shapes)
 *   - `{ items: RecordWithMeta[] }`                (defensive — paginated)
 *
 * Returns `[]` for any unrecognized shape — callers should treat absence as
 * "empty result", not "error" — after reporting it (see `reportUnexpectedShape`).
 */
export const parseRecordCollection = (raw: unknown): RecordWithMeta[] => {
  if (Array.isArray(raw)) {
    const records = raw
      .map(toRecordWithMeta)
      .filter((record): record is RecordWithMeta => record !== null);

    if (records.length !== raw.length) {
      // A recognised list carrying entries we could not read. Dropping them is
      // still the right recovery — the rows we DID understand render — but a
      // silently shorter page is indistinguishable from a shorter response.
      console.error(
        `[businessObjects] Dropped ${raw.length - records.length} unreadable collection entries`,
      );
    }

    return records;
  }

  if (isPlainObject(raw)) {
    const root = raw;

    if (Array.isArray(root.data)) {
      return root.data
        .map(toRecordWithMeta)
        .filter((record): record is RecordWithMeta => record !== null);
    }
    if (Array.isArray(root.items)) {
      return root.items.filter(isRecordWithMeta);
    }
    if (isPlainObject(root.data)) {
      const nested = root.data;
      if (Array.isArray(nested.data)) {
        return nested.data.filter(isRecordWithMeta);
      }
      if (Array.isArray(nested.items)) {
        return nested.items.filter(isRecordWithMeta);
      }
    }
  }

  reportUnexpectedShape("collection", raw);

  return [];
};

/**
 * Coerce a record-service detail response to a single `RecordWithMeta`,
 * or `null` if the shape is unrecognized.
 *
 * Accepts the same envelopes as `parseRecordCollection` but for a single
 * record.
 */
export const parseRecordDetail = (raw: unknown): RecordWithMeta | null => {
  if (!isPlainObject(raw)) {
    reportUnexpectedShape("detail", raw);

    return null;
  }

  // Prefer unwrapping when the inner value is itself a metadata envelope —
  // covers `{ data: { data, metadata } }` from once-wrapped responses.
  if (isMetadataEnvelope(raw.data)) {
    return raw.data;
  }

  if (isMetadataEnvelope(raw)) {
    return raw;
  }

  // No metadata anywhere; fall back to the looser "has a data field"
  // detection so callers that don't care about audit fields still get the
  // BO body.
  if (isRecordWithMeta(raw.data)) {
    return raw.data;
  }
  if (isRecordWithMeta(raw)) {
    return raw;
  }

  reportUnexpectedShape("detail", raw);

  return null;
};

/** Lift the inner `data` object out of a record-or-envelope. Useful when you
 *  only want the BO fields and don't care about the audit metadata. */
export const extractDataRecord = (item: unknown): Record<string, unknown> | null => {
  const detail = parseRecordDetail(item);
  return detail?.data ?? null;
};

/** `recordState: DELETED`. Metadata list routes still return the row; a read by id answers 404. */
export const isSoftDeletedRecord = (item: unknown): boolean => {
  const state = parseRecordDetail(item)?.metadata?.recordState;

  return typeof state === "string" && state.toUpperCase() === "DELETED";
};

/**
 * Resolve the primary key of a record. The BE places the PK in different
 * locations depending on the route and tenant flavor (top-level, `data.id`,
 * `data.pk`, or `metadata.id`), so we probe all of them.
 */
export const extractRecordPk = (item: unknown): string | null => {
  if (!isPlainObject(item)) {
    return null;
  }
  const top = item;
  const data = isPlainObject(top.data) ? top.data : null;
  const meta = isPlainObject(top.metadata) ? top.metadata : null;

  const candidate = top.id ?? top.pk ?? data?.id ?? data?.pk ?? meta?.id ?? meta?.pk;

  if (typeof candidate === "string" && candidate.trim()) {
    return candidate;
  }
  return null;
};

/**
 * Normalize audit metadata from a `RecordWithMeta` payload, applying the same
 * field-name aliasing as `normalizeBusinessObjectMetadata` (e.g. the BE typo
 * `updateAt` → `updatedAt`).
 *
 * Accepts both the metadata-route shape `{ metadata: {...} }` and the bare
 * `{...}` shape so callers don't have to know which they're holding.
 */
export const extractAuditMetadata = (raw: unknown): BusinessObjectMetadata => {
  if (!isPlainObject(raw)) {
    return {};
  }
  const candidate = isPlainObject(raw.metadata) ? raw.metadata : raw;
  const metadata = normalizeBusinessObjectMetadata(candidate);
  if (metadata.capabilities) {
    return metadata;
  }
  const fromEnvelope = parseRecordCapabilities(raw);
  return fromEnvelope ? { ...metadata, capabilities: fromEnvelope } : metadata;
};
